import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Text } from "@core/index";
import { FileUploader } from "@core/file-uploader";
import { CEFRSelector } from "@core/cefr-selector";
import DateTimePicker from "@core/datepicker/DateTimePicker";
import OutputLanguageSelector from "@features/tools/components/create-modal/OutputLanguageSelector";
import enFlag from "@packages/assets/en.png";
import deFlag from "@packages/assets/de.png";
import {
  CreatePronunciationAssignmentRequestDtoCefrLevel,
  CreatePronunciationAssignmentRequestDtoContentLanguage,
  CreatePronunciationAssignmentRequestDtoAccent,
} from "@packages/api/madlenAPI.schemas";
import {
  useCreateAssignment,
  useGetAssignment,
  useUpdateItems,
  useAssignPronunciationAssignment,
} from "@packages/api/madlenAPI";
// import { getEnvironment } from "@packages/utils";
import {
  Plus,
  Trash2,
  Link2,
  Copy,
  ExternalLink,
  PlayCircle,
  Share2,
  Users,
} from "lucide-react";
import Modal from "@core/modal";
import { ClassroomAndStudentSelector } from "@features/assignments/SingleAssignment/componnets/ClassroomAndStudentSelector";
import { useToast } from "@packages/contexts/ToastContext";

type GenerationMethod = "prompt" | "file";
type AssessmentScope = "word" | "sentence";

const CreatePronunciationAssessmentView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  const [scope, setScope] = useState<AssessmentScope>("sentence");
  const [generationMethod, setGenerationMethod] =
    useState<GenerationMethod>("prompt");
  const [prompt, setPrompt] = useState<string>("");
  const [teacherDocumentId, setTeacherDocumentId] = useState<
    number | undefined
  >(undefined);
  const [sentenceCount, setSentenceCount] = useState<number>(3);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [sentences, setSentences] = useState<string[]>([]);
  const [initializedSentences, setInitializedSentences] =
    useState<boolean>(false);
  const [shareLink, setShareLink] = useState<string>("");
  const [copying, setCopying] = useState(false);
  const [cefrLevel, setCefrLevel] =
    useState<CreatePronunciationAssignmentRequestDtoCefrLevel>(
      CreatePronunciationAssignmentRequestDtoCefrLevel.PRE_A1,
    );
  const [createdId, setCreatedId] = useState<number | undefined>(undefined);
  const [fakeProgress, setFakeProgress] = useState<number>(0);
  const MAX_SENTENCES = 15;

  // Student assignment modal state
  const [showAssignModal, setShowAssignModal] = useState<boolean>(false);
  const [selectedStudents, setSelectedStudents] = useState<
    {
      classroomId: number;
      studentIds: number[];
    }[]
  >([]);

  // Date management for end date
  const oneWeekLater = new Date();
  oneWeekLater.setDate(oneWeekLater.getDate() + 7);
  const [endDate, setEndDate] = useState<string>(oneWeekLater.toISOString());

  const createMutation = useCreateAssignment();
  const { mutateAsync: createAssignmentMutate } = createMutation;
  const isCreating = createMutation.status === "pending";

  const assignmentQuery = useGetAssignment(createdId!, {
    query: {
      enabled: !!createdId,
      refetchInterval: (data: any) => {
        // Stop polling once items are present
        const hasItems =
          Array.isArray((data as any)?.items) &&
          (data as any)?.items?.length > 0;
        return hasItems ? false : 2000;
      },
    },
  });

  const updateItemsMutation = useUpdateItems();
  const { mutateAsync: updateItemsMutate } = updateItemsMutation;
  const isUpdating = updateItemsMutation.status === "pending";

  // Assignment mutation
  const { mutate: assignAssessment, isPending: isAssigning } =
    useAssignPronunciationAssignment({
      mutation: {
        onSuccess: () => {
          setShowAssignModal(false);
          setSelectedStudents([]);
          addToast({
            title: t("pronunciationAssessment.notification.assignSuccess"),
            variant: "success",
            duration: 3000,
          });
        },
        onError: () => {
          addToast({
            title: t("notifications.assignment.assignError"),
            description: t("notifications.assignment.assignErrorDescription"),
            variant: "error",
            duration: 3000,
          });
        },
      },
    });

  const mappedContentLanguage: CreatePronunciationAssignmentRequestDtoContentLanguage =
    useMemo(() => {
      const lang = (i18n.language || "en").split("-")[0];
      const allowed = ["tr", "en", "de", "fr", "it", "es"] as const;
      return (
        allowed.includes(lang as any) ? lang : "en"
      ) as CreatePronunciationAssignmentRequestDtoContentLanguage;
    }, [i18n.language]);

  const defaultAccent: CreatePronunciationAssignmentRequestDtoAccent =
    CreatePronunciationAssignmentRequestDtoAccent.BRITISH;
  const [accent, setAccent] =
    useState<CreatePronunciationAssignmentRequestDtoAccent>(defaultAccent);
  const [contentLanguage, setContentLanguage] =
    useState<CreatePronunciationAssignmentRequestDtoContentLanguage>(
      mappedContentLanguage,
    );

  useEffect(() => {
    if (
      !initializedSentences &&
      Array.isArray((assignmentQuery.data as any)?.items)
    ) {
      const items = (assignmentQuery.data as any)?.items as any[];
      const texts = items
        ?.map((item) => (typeof item === "string" ? item : item?.text))
        ?.filter((x: any) => typeof x === "string" && x.trim().length > 0);
      if (texts && texts.length > 0) {
        setSentences(texts as string[]);
        setInitializedSentences(true);
      }
    }
  }, [assignmentQuery.data, initializedSentences]);

  // Fake progress animation
  useEffect(() => {
    if (!initializedSentences && !!createdId) {
      setFakeProgress(0);
      const interval = setInterval(() => {
        setFakeProgress((prev) => {
          if (prev >= 95) {
            // Stop at 95% and let real data loading complete
            return prev;
          }
          // Slower progress at the beginning, faster in the middle, slower at the end
          const increment = prev < 30 ? 2 : prev < 70 ? 4 : 1;
          return Math.min(prev + increment, 95);
        });
      }, 150);

      return () => clearInterval(interval);
    } else {
      setFakeProgress(0);
    }
  }, [initializedSentences, createdId]);

  function validateStepOne(): boolean {
    const newErrors: Record<string, string> = {};
    if (generationMethod === "prompt" && !(prompt || "").trim()) {
      newErrors.prompt = t("common.required");
    }
    if (generationMethod === "file" && !teacherDocumentId) {
      newErrors.teacherDocumentId = t("common.required");
    }
    if (!sentenceCount || sentenceCount < 1) {
      newErrors.sentenceCount = t("common.required");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleContinue() {
    if (currentStep === 1) {
      if (!validateStepOne()) return;
      try {
        const result = await createAssignmentMutate({
          data: {
            contentLanguage,
            accent,
            generationType: scope === "word" ? "WORD" : "SENTENCE",
            generationCount: sentenceCount,
            cefrLevel,
            topic: generationMethod === "prompt" ? prompt : undefined,
            teacherDocumentId:
              generationMethod === "file" && teacherDocumentId
                ? teacherDocumentId
                : undefined,
            endDate,
          },
        });
        if (result?.id) {
          setCreatedId(result.id);
        }
        setCurrentStep(2);
      } catch (e) {
        // noop; axiosConfig toast handles error
      }
      return;
    }
    if (currentStep === 2) {
      if (!createdId) return;
      const trimmed = sentences.map((s) => s.trim());
      const nonEmpty = trimmed.filter((s) => s.length > 0);
      if (nonEmpty.length === 0) {
        setErrors({ sentences: t("common.required") });
        return;
      }

      // When sentence-level is selected, ensure each non-empty sentence has at least two words
      if (scope === "sentence") {
        const perSentenceErrors: Record<string, string> = {};
        trimmed.forEach((s, idx) => {
          if (s.length > 0) {
            const wordCount = s.split(/\s+/).filter(Boolean).length;
            if (wordCount < 2) {
              perSentenceErrors[`sentences.${idx}`] = t(
                "pronunciationAssessment.create.validation.minTwoWords",
              );
            }
          }
        });
        if (Object.keys(perSentenceErrors).length > 0) {
          setErrors(perSentenceErrors);
          return;
        }
      }

      // When word-level is selected, ensure each non-empty item is a single word (no spaces)
      if (scope === "word") {
        const perSentenceErrors: Record<string, string> = {};
        trimmed.forEach((s, idx) => {
          if (s.length > 0) {
            const tokenCount = s.split(/\s+/).filter(Boolean).length;
            if (tokenCount !== 1) {
              perSentenceErrors[`sentences.${idx}`] = t(
                "pronunciationAssessment.create.validation.onlySingleWord",
              );
            }
          }
        });
        if (Object.keys(perSentenceErrors).length > 0) {
          setErrors(perSentenceErrors);
          return;
        }
      }
      try {
        const originalItems: Array<string | { id: number; text: string }> =
          (Array.isArray((assignmentQuery.data as any)?.items)
            ? ((assignmentQuery.data as any)?.items as Array<
                string | { id: number; text: string }
              >)
            : []) || [];

        const items = trimmed
          .filter((s) => s.length > 0)
          .map((text, index) => {
            const original = originalItems[index];
            const originalId =
              original && typeof original === "object"
                ? (original as any).id
                : undefined;
            return {
              id: typeof originalId === "number" ? originalId : index + 1,
              text,
            };
          });

        const updated = await updateItemsMutate({
          id: createdId,
          data: {
            items,
            title: "",
          },
        });
        const publicCode =
          (updated as any)?.publicCode ||
          (assignmentQuery.data as any)?.publicCode;
        if (publicCode) {
          const url = `${window.location.origin}/pronunciation-student/v2/${publicCode}`;
          setShareLink(url);
        }
        setCurrentStep(3);
      } catch (e) {
        // noop
      }
      return;
    }
  }

  const handleAssignStudents = () => {
    if (selectedStudents.length === 0) {
      addToast({
        title: t("assignments.noStudentsSelected"),
        variant: "warning",
        duration: 3000,
      });
      return;
    }

    if (!createdId) return;

    const assignData = {
      assignedStudentsWithClassrooms: selectedStudents,
      startDate: new Date().toISOString(),
      endDate,
    };

    assignAssessment({
      id: createdId,
      data: assignData,
    });
  };

  function handleBack() {
    if (currentStep === 3) {
      setCurrentStep(2);
      return;
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-gray-50 dark:bg-dark-500 mt-5 rounded-tl-3xl">
      <div className="flex items-center px-6 pt-8 pb-4">
        <button
          onClick={() => navigate("/content/pronunciation-assessment")}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mr-4 transition-colors"
        ></button>
        <Text variant="h1" className="text-2xl font-semibold flex-1">
          {t("pronunciationAssessment.create.title")}
        </Text>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-4">
          {currentStep === 1 && (
            <>
              <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
                <div className="flex items-center mb-6">
                  <div>
                    <Text
                      variant="h2"
                      className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                    >
                      {t(
                        "pronunciationAssessment.create.assessmentScope.title",
                      )}
                    </Text>
                    <Text
                      variant="p"
                      className="text-sm text-gray-600 dark:text-gray-400"
                    >
                      {t(
                        "pronunciationAssessment.create.assessmentScope.description",
                      )}
                    </Text>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <button
                    onClick={() => setScope("word")}
                    className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                      scope === "word"
                        ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 shadow-sm"
                        : "border-gray-200 dark:border-dark-400 hover:border-gray-300 dark:hover:border-dark-300"
                    }`}
                    title={
                      t(
                        "pronunciationAssessment.create.assessmentScope.wordOption",
                      ) as string
                    }
                  >
                    <div className="text-left">
                      <div
                        className={`font-medium inline-flex items-center gap-2 ${
                          scope === "word"
                            ? "text-cyan-700 dark:text-cyan-300"
                            : "text-gray-900 dark:text-gray-100"
                        }`}
                      >
                        {t(
                          "pronunciationAssessment.create.assessmentScope.wordOption",
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {t(
                          "pronunciationAssessment.create.assessmentScope.wordHint",
                        )}
                      </div>
                    </div>
                    {scope === "word" && (
                      <div className="absolute top-2 right-2 w-3 h-3 bg-cyan-500 rounded-full"></div>
                    )}
                  </button>

                  <button
                    onClick={() => setScope("sentence")}
                    className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                      scope === "sentence"
                        ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 shadow-sm"
                        : "border-gray-200 dark:border-dark-400 hover:border-gray-300 dark:hover:border-dark-300"
                    }`}
                  >
                    <div className="text-left">
                      <div
                        className={`font-medium ${
                          scope === "sentence"
                            ? "text-cyan-700 dark:text-cyan-300"
                            : "text-gray-900 dark:text-gray-100"
                        }`}
                      >
                        {t(
                          "pronunciationAssessment.create.assessmentScope.sentenceOption",
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {t(
                          "pronunciationAssessment.create.assessmentScope.sentenceHint",
                        )}
                      </div>
                    </div>
                    {scope === "sentence" && (
                      <div className="absolute top-2 right-2 w-3 h-3 bg-cyan-500 rounded-full"></div>
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
                <div className="flex items-center mb-6">
                  <div>
                    <Text
                      variant="h2"
                      className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                    >
                      {t("pronunciationAssessment.create.generation.title")}
                    </Text>
                    <Text
                      variant="p"
                      className="text-sm text-gray-600 dark:text-gray-400"
                    >
                      {t(
                        "pronunciationAssessment.create.generation.description",
                      )}
                    </Text>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <button
                    onClick={() => setGenerationMethod("prompt")}
                    className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                      generationMethod === "prompt"
                        ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 shadow-sm"
                        : "border-gray-200 dark:border-dark-400 hover:border-gray-300 dark:hover:border-dark-300"
                    }`}
                  >
                    <div className="text-left">
                      <div
                        className={`font-medium ${
                          generationMethod === "prompt"
                            ? "text-cyan-700 dark:text-cyan-300"
                            : "text-gray-900 dark:text-gray-100"
                        }`}
                      >
                        {t(
                          "pronunciationAssessment.create.generation.promptOption",
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {t(
                          "pronunciationAssessment.create.generation.promptHint",
                        )}
                      </div>
                    </div>
                    {generationMethod === "prompt" && (
                      <div className="absolute top-2 right-2 w-3 h-3 bg-cyan-500 rounded-full"></div>
                    )}
                  </button>

                  <button
                    onClick={() => setGenerationMethod("file")}
                    className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                      generationMethod === "file"
                        ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 shadow-sm"
                        : "border-gray-200 dark:border-dark-400 hover:border-gray-300 dark:hover:border-dark-300"
                    }`}
                  >
                    <div className="text-left">
                      <div
                        className={`font-medium ${
                          generationMethod === "file"
                            ? "text-cyan-700 dark:text-cyan-300"
                            : "text-gray-900 dark:text-gray-100"
                        }`}
                      >
                        {t(
                          "pronunciationAssessment.create.generation.fileOption",
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {t(
                          "pronunciationAssessment.create.generation.fileHint",
                        )}
                      </div>
                    </div>
                    {generationMethod === "file" && (
                      <div className="absolute top-2 right-2 w-3 h-3 bg-cyan-500 rounded-full"></div>
                    )}
                  </button>
                </div>

                {generationMethod === "prompt" && (
                  <div className="space-y-3">
                    <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t(
                        "pronunciationAssessment.create.generation.promptLabel",
                      )}
                      <span className="ml-1">*</span>
                    </label>
                    <textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder={t(
                        "pronunciationAssessment.create.generation.promptPlaceholder",
                      )}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-dark-500 dark:border-dark-500 dark:text-white transition-colors ${
                        errors.prompt
                          ? "border-red-500"
                          : "border-gray-300 dark:border-dark-400"
                      }`}
                      rows={4}
                    />
                    {errors.prompt && (
                      <Text
                        variant="p"
                        className="text-red-500 text-sm flex items-center gap-1"
                      >
                        {errors.prompt}
                      </Text>
                    )}
                  </div>
                )}

                {generationMethod === "file" && (
                  <div className="space-y-3">
                    <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t(
                        "pronunciationAssessment.create.generation.uploadFileLabel",
                      )}
                      <span className="ml-1">*</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-300 dark:border-dark-400 rounded-lg p-6 bg-gray-50 dark:bg-dark-500">
                      <FileUploader
                        onUploadSuccess={(_unused, response) =>
                          setTeacherDocumentId(response?.id)
                        }
                      />
                    </div>
                    {errors.teacherDocumentId && (
                      <Text
                        variant="p"
                        className="text-red-500 text-sm flex items-center gap-1"
                      >
                        {errors.teacherDocumentId}
                      </Text>
                    )}
                  </div>
                )}

                {/* CEFR Level inside Generation Method */}
                <div className="mt-6">
                  <div className="mb-3">
                    <Text
                      variant="h2"
                      className="text-base font-semibold text-gray-900 dark:text-gray-100"
                    >
                      {t("pronunciationAssessment.create.cefrLevel.title")}
                    </Text>
                    <Text
                      variant="p"
                      className="text-xs text-gray-600 dark:text-gray-400"
                    >
                      {t(
                        "pronunciationAssessment.create.cefrLevel.description",
                      )}
                    </Text>
                  </div>
                  <CEFRSelector
                    selectedLevel={cefrLevel}
                    onLevelChange={(level) => setCefrLevel(level)}
                    themeColor="blue"
                    className="mt-1"
                  />
                </div>

                <div className="mt-6">
                  <Text
                    variant="h2"
                    className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3"
                  >
                    {t("podcast.endDate")}
                  </Text>
                  <DateTimePicker
                    value={endDate}
                    onChange={setEndDate}
                    minDate={new Date()}
                    placeholder={t("podcast.selectEndDate")}
                    className="w-full"
                  />
                </div>
              </div>
              {/* Settings moved under Assessment Scope */}
              <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
                <Text
                  variant="h2"
                  className="text-lg font-semibold mb-6 text-gray-900 dark:text-gray-100"
                >
                  {t("pronunciationAssessment.create.settings.title")}
                </Text>

                <div className="space-y-8">
                  {/* Content language and accent section */}
                  <div className="bg-gray-50 dark:bg-dark-500 rounded-xl p-5 border border-gray-100 dark:border-dark-400">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Content language selector */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t("interactive_material.create.contentLanguage")}
                        </label>
                        <OutputLanguageSelector
                          selectedLanguage={contentLanguage}
                          onLanguageChange={(lang: string) =>
                            setContentLanguage(
                              lang as CreatePronunciationAssignmentRequestDtoContentLanguage,
                            )
                          }
                          themeColor="blue"
                          languages={[
                            { code: "en", name: "English", flag: enFlag },
                            { code: "de", name: "Deutsch", flag: deFlag },
                          ]}
                        />
                      </div>

                      {/* Accent selection - only show for English */}
                      {contentLanguage === "en" && (
                        <div className="space-y-3">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t("pronunciationAssessment.create.accent.title")}
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() =>
                                setAccent(
                                  CreatePronunciationAssignmentRequestDtoAccent.BRITISH,
                                )
                              }
                              className={`flex items-center justify-center gap-3 p-3 rounded-lg border-2 transition-all duration-200 ${
                                accent ===
                                CreatePronunciationAssignmentRequestDtoAccent.BRITISH
                                  ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 shadow-sm"
                                  : "border-gray-200 dark:border-dark-400 hover:border-gray-300 dark:hover:border-dark-300 hover:shadow-sm"
                              }`}
                            >
                              <span className="text-2xl">🇬🇧</span>
                              <span
                                className={`${
                                  accent ===
                                  CreatePronunciationAssignmentRequestDtoAccent.BRITISH
                                    ? "text-cyan-700 dark:text-cyan-300"
                                    : "text-gray-900 dark:text-gray-100"
                                } text-sm font-medium`}
                              >
                                {t(
                                  "pronunciationAssessment.create.accent.british",
                                )}
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setAccent(
                                  CreatePronunciationAssignmentRequestDtoAccent.AMERICAN,
                                )
                              }
                              className={`flex items-center justify-center gap-3 p-3 rounded-lg border-2 transition-all duration-200 ${
                                accent ===
                                CreatePronunciationAssignmentRequestDtoAccent.AMERICAN
                                  ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 shadow-sm"
                                  : "border-gray-200 dark:border-dark-400 hover:border-gray-300 dark:hover:border-dark-300 hover:shadow-sm"
                              }`}
                            >
                              <span className="text-2xl">🇺🇸</span>
                              <span
                                className={`${
                                  accent ===
                                  CreatePronunciationAssignmentRequestDtoAccent.AMERICAN
                                    ? "text-cyan-700 dark:text-cyan-300"
                                    : "text-gray-900 dark:text-gray-100"
                                } text-sm font-medium`}
                              >
                                {t(
                                  "pronunciationAssessment.create.accent.american",
                                )}
                              </span>
                            </button>
                          </div>
                          <Text
                            variant="p"
                            className="text-xs text-gray-600 dark:text-gray-400"
                          >
                            {t(
                              "pronunciationAssessment.create.accent.description",
                            )}
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sentence count section */}
                  <div className="bg-gray-50 dark:bg-dark-500 rounded-xl p-5 border border-gray-100 dark:border-dark-400">
                    <div className="space-y-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t(
                              "pronunciationAssessment.create.settings.sentenceWordCount",
                            )}
                          </label>
                          <Text
                            variant="p"
                            className="text-xs text-gray-500 dark:text-gray-400"
                          >
                            {t(
                              "pronunciationAssessment.create.settings.maxSentences",
                            )}
                            : 10
                          </Text>
                        </div>
                        <div className="px-4 py-2 rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-lg font-bold min-w-[3rem] text-center">
                          {sentenceCount}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="relative px-2">
                          <input
                            type="range"
                            min={1}
                            max={10}
                            value={sentenceCount}
                            onChange={(e) =>
                              setSentenceCount(Number(e.target.value))
                            }
                            className="w-full h-2 bg-gradient-to-r from-cyan-200 to-gray-200 dark:from-cyan-800 dark:to-gray-600 rounded-full appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-dark-600"
                            style={{
                              background: `linear-gradient(to right, #06b6d4 0%, #06b6d4 ${(sentenceCount - 1) * 11.11}%, #e5e7eb ${(sentenceCount - 1) * 11.11}%, #e5e7eb 100%)`,
                            }}
                          />
                          <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 mt-2">
                            <span>1</span>
                            <span>10</span>
                          </div>
                        </div>
                      </div>

                      {errors.sentenceCount && (
                        <Text variant="p" className="text-red-500 text-sm">
                          {errors.sentenceCount}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {currentStep === 2 && (
            <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
              <div className="flex items-center justify-between mb-6">
                <Text
                  variant="h2"
                  className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                >
                  {t("pronunciationAssessment.create.preview.title")}
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  rounded="full"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() =>
                    sentences.length < MAX_SENTENCES &&
                    setSentences((prev) => [...prev, ""])
                  }
                  disabled={sentences.length >= MAX_SENTENCES}
                >
                  {t("common.addMore")}
                </Button>
              </div>
              {!initializedSentences && !!createdId && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-6 h-6 bg-cyan-500 rounded-full animate-pulse"></div>
                    <Text
                      variant="p"
                      className="text-sm text-gray-600 dark:text-gray-400"
                    >
                      {t("pronunciationAssessment.create.preview.generating")}
                    </Text>
                  </div>

                  {/* Loading skeleton for sentences */}
                  <div className="space-y-3">
                    {Array.from({ length: sentenceCount }).map((_, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-dark-400 animate-pulse"></div>
                        <div className="flex-1 h-10 bg-gray-200 dark:bg-dark-400 rounded-lg animate-pulse"></div>
                        <div className="w-8 h-8 bg-gray-200 dark:bg-dark-400 rounded-lg animate-pulse"></div>
                      </div>
                    ))}
                  </div>

                  {/* Progress indicator */}
                  <div className="mt-6 p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800">
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-cyan-500 rounded-full animate-bounce"></div>
                      <div className="flex-1">
                        <div className="h-2 bg-cyan-200 dark:bg-cyan-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-cyan-500 rounded-full transition-all duration-300 ease-out"
                            style={{ width: `${fakeProgress}%` }}
                          ></div>
                        </div>
                      </div>
                      <Text
                        variant="p"
                        className="text-xs text-cyan-700 dark:text-cyan-300 font-medium"
                      >
                        {Math.round(fakeProgress)}%
                      </Text>
                    </div>
                  </div>
                </div>
              )}
              <div className="space-y-3">
                {sentences.map((sentence, index) => {
                  const sentenceErrorKey = `sentences.${index}`;
                  const sentenceError = errors[sentenceErrorKey];
                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 flex items-center justify-center text-sm font-semibold">
                          {index + 1}
                        </div>
                        <input
                          type="text"
                          value={sentence}
                          onChange={(e) => {
                            let value = e.target.value;
                            if (scope === "word") {
                              value = value.replace(/\s+/g, "");
                            }
                            const updated = [...sentences];
                            updated[index] = value;
                            setSentences(updated);
                          }}
                          onKeyDown={(e) => {
                            if (
                              scope === "word" &&
                              (e.key === " " || e.key === "Spacebar")
                            ) {
                              e.preventDefault();
                            }
                          }}
                          className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-dark-500 dark:border-dark-400 dark:text-white transition-colors ${
                            sentenceError ? "border-red-500" : "border-gray-300"
                          }`}
                        />
                        <Button
                          size="sm"
                          leftIcon={<Trash2 className="w-4 h-4" />}
                          onClick={() =>
                            setSentences((prev) =>
                              prev.length > 1
                                ? prev.filter((_, i) => i !== index)
                                : prev,
                            )
                          }
                          disabled={sentences.length <= 1}
                        ></Button>
                      </div>
                      {sentenceError && (
                        <Text variant="p" className="text-red-500 text-xs">
                          {sentenceError}
                        </Text>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
              <Text
                variant="h2"
                className="text-lg font-semibold mb-1 text-gray-900 dark:text-gray-100"
              >
                {t("pronunciationAssessment.create.shareAndAssign.title")}
              </Text>
              <Text
                variant="p"
                className="text-sm text-gray-600 dark:text-gray-400 mb-4"
              >
                {t("pronunciationAssessment.create.shareAndAssign.description")}
              </Text>

              <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-dark-500 border border-gray-200 dark:border-dark-400 mb-4">
                <div className="w-9 h-9 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 flex items-center justify-center">
                  <Link2 className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  readOnly
                  value={shareLink}
                  className="flex-1 px-3 py-2 border rounded-lg focus:outline-none dark:bg-dark-500 dark:border-dark-400 dark:text-white border-gray-300"
                />
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<Copy className="w-4 h-4" />}
                  onClick={async () => {
                    try {
                      setCopying(true);
                      await navigator.clipboard.writeText(shareLink);
                      // Reset after 2 seconds
                      setTimeout(() => setCopying(false), 2000);
                    } catch {
                      setCopying(false);
                    }
                  }}
                  className={
                    copying
                      ? "bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300"
                      : ""
                  }
                >
                  {copying ? (
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      {t("common.copied")}
                    </span>
                  ) : (
                    t("common.copy")
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<ExternalLink className="w-4 h-4" />}
                  onClick={() => window.open(shareLink, "_blank")}
                >
                  Open
                </Button>
              </div>

              {/* OR Separator */}
              <div className="flex items-center mb-4">
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600"></div>
                <span className="px-4 text-sm text-gray-500 dark:text-gray-400 font-medium">
                  {t("common.or")}
                </span>
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600"></div>
              </div>

              {/* Student Assignment Section */}
              <div className="p-4 rounded-xl border border-cyan-200 dark:border-cyan-800 bg-cyan-50/50 dark:bg-cyan-900/20 mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-100 dark:bg-cyan-900/50 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <div className="flex-1">
                    <Text
                      variant="h3"
                      className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1"
                    >
                      {t("pronunciationAssessment.create.share.assignStudents")}
                    </Text>
                    <Text
                      variant="p"
                      className="text-sm text-gray-600 dark:text-gray-400 mb-3"
                    >
                      {t(
                        "pronunciationAssessment.create.share.assignDescription",
                      )}
                    </Text>
                    <Button
                      size="sm"
                      rounded="lg"
                      onClick={() => setShowAssignModal(true)}
                      leftIcon={<Share2 className="w-4 h-4" />}
                      className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white"
                    >
                      {t("pronunciationAssessment.create.share.assignButton")}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Button
                  size="md"
                  rounded="sm"
                  onClick={() => window.open(shareLink, "_blank")}
                  leftIcon={<PlayCircle className="w-5 h-5" />}
                  className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white"
                >
                  {t("pronunciationAssessment.create.share.tryDemo")}
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            {currentStep === 3 && (
              <Button
                size="md"
                rounded="sm"
                variant="outline"
                onClick={handleBack}
              >
                {t("common.back")}
              </Button>
            )}
            <Button
              size="md"
              rounded="sm"
              onClick={() => {
                if (currentStep < 3) {
                  handleContinue();
                } else {
                  const id = createdId || (assignmentQuery.data as any)?.id;
                  if (id) {
                    navigate(`/content/pronunciation-assessment/${id}`);
                  }
                }
              }}
              className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white"
              disabled={isCreating || isUpdating}
            >
              {currentStep < 3
                ? t("common.continue")
                : t("pronunciationAssessment.create.share.finish")}
            </Button>
          </div>
        </div>
      </div>

      {/* Student Assignment Modal */}
      <Modal
        show={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        title={t("pronunciationAssessment.create.share.assignModalTitle")}
        outerClassName="max-w-lg animate-modal-enter max-h-[90vh] overflow-y-auto"
      >
        <div className="p-4">
          <div className="mb-6 max-h-[60vh] overflow-y-auto">
            <ClassroomAndStudentSelector
              selectedStudents={selectedStudents}
              setSelectedStudents={setSelectedStudents}
              shouldUseModal={false}
            />
          </div>

          <div className="flex gap-2 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="outline"
              size="sm"
              rounded="lg"
              className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
              onClick={() => setShowAssignModal(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              size="sm"
              rounded="lg"
              className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white"
              onClick={handleAssignStudents}
              disabled={isAssigning || selectedStudents.length === 0}
            >
              {isAssigning ? t("common.loading") : t("common.assign")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CreatePronunciationAssessmentView;
